import { addShortcodeToEditor, getShortcodeWrapper } from "@/assets/utilities/tinymce/shortodeAddition";
import { getLiveChatTemplate, getLiveChatV2Template } from "@/assets/utilities/tinymce/shortcodes/templates";
import { parseToHtml } from "@/utility/parseToHtml";
import { serializeDynamicComponents, visualizeDynamicComponents } from "@/assets/utilities/tinymce/visualization";
import { convertAssetsPathToRelativeLinks } from "@/utility/convertAssetsPaths";
import { traverseTree } from "@/utility/traverseTree";
import { findInvalidTelHrefsDeep } from "@/utility/validateLiveChatTelHrefs";
import { showTelConfirmModal } from "@/assets/utilities/tinymce/helpers/telConfirmModal";
import i18n from "@/i18n";
import { normalizeIds } from "@/components/shortcodes/livechatV2/treeGraph";
import { editLiveChatV2Config } from "@/assets/utilities/tinymce/shortcodeConfigs/liveChatV2Config";

function serializeContent(obj) {
  if (typeof obj !== "object" || obj === null) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => serializeContent(item));
  } else {
    const newObj = {};
    Object.keys(obj).forEach(key => {
      if (key === "_telHrefError") return;
      if (key === "content") {
        const content = parseToHtml(obj[key]);
        serializeDynamicComponents(content);
        newObj[key] = content.body.innerHTML;
      } else if (key === "text") {
        const content = parseToHtml(obj[key]);
        serializeDynamicComponents(content);
        newObj[key] = content.body.innerHTML;
      } else {
        newObj[key] = serializeContent(obj[key]);
      }
    });

    return newObj;
  }
}

function visualizeContent(obj) {
  if (typeof obj !== "object" || obj === null) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => visualizeContent(item));
  } else {
    const newObj = {};
    Object.keys(obj).forEach(key => {
      if (key === "content") {
        const content = parseToHtml(obj[key]);
        visualizeDynamicComponents(content);
        newObj[key] = content.body.innerHTML;
      } else if (key === "text") {
        const content = parseToHtml(obj[key]);
        visualizeDynamicComponents(content);
        newObj[key] = content.body.innerHTML;
      } else {
        newObj[key] = visualizeContent(obj[key]);
      }
    });

    return newObj;
  }
}

const editLiveChatConfig = (editor, shortcodeWrapper) => {
  let currentData = null;

  const liveChatWindow = editor.windowManager.openUrl({
    title: "Live Chat",
    url: "/live-chat-dialog",
    buttons: [
      {
        type: "cancel",
        name: "close",
        text: "Cancel",
      },
      {
        type: "custom",
        name: "submit",
        text: "Submit",
        primary: true,
      },
    ],
    onAction: function (instance, message) {
      const save = () => {
        editor.windowManager.alert("Success!");
        traverseTree(currentData.messages, message => {
          if (message.content) {
            message.content = convertAssetsPathToRelativeLinks(message.content)?.body?.innerHTML;
          }

          if (message.text) {
            message.text = convertAssetsPathToRelativeLinks(message.text)?.body?.innerHTML;
          }

          if (message.value) {
            message.value = convertAssetsPathToRelativeLinks(message.value)?.body?.innerHTML;
          }
        });
        const serializedData = serializeContent(currentData);
        const encodedData = btoa(unescape(encodeURIComponent(JSON.stringify(serializedData))));

        shortcodeWrapper.setAttribute("data-live-chat-data", encodedData);
        // We have to call change function here to updateMainContent in form component
        editor.fire("change");
        instance.close();
      };

      const { valid, errors } = findInvalidTelHrefsDeep(currentData);
      if (!valid) {
        showTelConfirmModal(
          i18n.t("shortcodes.live_chat.invalid_tel_href_confirm", { count: errors.length }),
          errors.map(e => e.invalidHrefs.join(", ")).join(", "),
        ).then(confirmed => {
          if (confirmed) save();
          else instance.sendMessage({ type: "telHrefValidationFailed" });
        });
        return;
      }

      save();
    },
    onMessage: (instance, message) => {
      if (message.mceAction === "requestData") {
        sendData();
      }

      if (message.mceAction === "updateLiveChatData") {
        currentData = message.data;
      }

      if (message.mceAction === "transformToV2") {
        transformToV2(message.data);
      }
    },
    width: window.innerWidth - 20,
    height: window.innerHeight - 20,
  });

  const transformToV2 = data => {
    const transform = () => {
      traverseTree(data.messages, message => {
        if (message.content) {
          message.content = convertAssetsPathToRelativeLinks(message.content)?.body?.innerHTML;
        }

        if (message.text) {
          message.text = convertAssetsPathToRelativeLinks(message.text)?.body?.innerHTML;
        }

        if (message.value) {
          message.value = convertAssetsPathToRelativeLinks(message.value)?.body?.innerHTML;
        }
      });
      normalizeIds(data.messages);
      const serializedData = serializeContent(data);
      const encodedData = btoa(unescape(encodeURIComponent(JSON.stringify(serializedData))));
      const placeholder = shortcodeWrapper.querySelector(".live-chat-shortcode");
      const v2Placeholder = parseToHtml(getLiveChatV2Template()).body.firstChild;

      editor.undoManager.transact(() => {
        editor.dom.setAttribs(placeholder, {
          src: v2Placeholder.getAttribute("src"),
          class: v2Placeholder.className,
        });
        shortcodeWrapper.setAttribute("data-wrapper-id", "live-chat-v2-shortcode");
        shortcodeWrapper.setAttribute("data-live-chat-data", encodedData);
      });
      editor.fire("change");
      liveChatWindow.close();
      editLiveChatV2Config(editor, shortcodeWrapper);
    };

    const { valid, errors } = findInvalidTelHrefsDeep(data);
    if (!valid) {
      showTelConfirmModal(
        i18n.t("shortcodes.live_chat.invalid_tel_href_confirm", { count: errors.length }),
        errors.map(e => e.invalidHrefs.join(", ")).join(", "),
      ).then(confirmed => {
        if (confirmed) transform();
        else liveChatWindow.sendMessage({ type: "telHrefValidationFailed" });
      });

      return;
    }

    transform();
  };

  const sendData = () => {
    const liveChatShortcodeData = shortcodeWrapper.getAttribute("data-live-chat-data");
    const parsedData = JSON.parse(decodeURIComponent(escape(window.atob(liveChatShortcodeData))));
    const content = visualizeContent(parsedData);

    liveChatWindow.sendMessage({
      type: "sendData",
      data: content,
    });
  };
};

const liveChatConfig = (tinymce, uuid, shortcodeOnClick) => {
  let currentData = null;

  tinymce.activeEditor.windowManager.openUrl({
    title: "Live Chat",
    url: "/live-chat-dialog?data=null",
    buttons: [
      {
        type: "cancel",
        name: "close",
        text: "Cancel",
      },
      {
        type: "custom",
        name: "submit",
        text: "Submit",
        primary: true,
      },
    ],
    onAction: function (instance) {
      const save = () => {
        const shortcodeWrapper = getShortcodeWrapper(
          uuid,
          shortcodeOnClick,
          getLiveChatTemplate(),
          "live-chat-shortcode",
        );

        traverseTree(currentData.messages, message => {
          if (message.content) {
            message.content = convertAssetsPathToRelativeLinks(message.content)?.body?.innerHTML;
          }

          if (message.text) {
            message.text = convertAssetsPathToRelativeLinks(message.text)?.body?.innerHTML;
          }

          if (message.value) {
            message.value = convertAssetsPathToRelativeLinks(message.value)?.body?.innerHTML;
          }
        });

        const shortcodeWrapperEl = parseToHtml(shortcodeWrapper).querySelector(".shortcode");
        const serializedData = serializeContent(currentData);
        const encodedData = btoa(unescape(encodeURIComponent(JSON.stringify(serializedData))));

        shortcodeWrapperEl.setAttribute("data-live-chat-data", encodedData);

        addShortcodeToEditor(shortcodeWrapperEl.outerHTML, uuid);
        instance.close();
      };

      const { valid, errors } = findInvalidTelHrefsDeep(currentData);
      if (!valid) {
        showTelConfirmModal(
          i18n.t("shortcodes.live_chat.invalid_tel_href_confirm", { count: errors.length }),
          errors.map(e => e.invalidHrefs.join(", ")).join(", "),
        ).then(confirmed => {
          if (confirmed) save();
          else instance.sendMessage({ type: "telHrefValidationFailed" });
        });
        return;
      }

      save();
    },
    onMessage: (instance, message) => {
      currentData = message.data;
    },
    width: window.innerWidth - 20,
    height: window.innerHeight - 20,
  });
};

export { liveChatConfig, editLiveChatConfig };
